import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter } from 'rxjs/operators';
import { AuthService } from '@app/core/auth/auth.service';
import { GlobalToastComponent } from '@app/shared/toast/global-toast.component';
import { GlobalConfirmDialogComponent } from '@app/shared/confirm/global-confirm-dialog.component';
import { LoadingOverlayComponent } from '@app/shared/loading/loading-overlay.component';
import { LoadingOverlayService } from '@app/core/services/loading-overlay.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    GlobalToastComponent,
    GlobalConfirmDialogComponent,
    LoadingOverlayComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly authService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly router = inject(Router);
  private readonly userService = inject(AuthService);
  private readonly loadingOverlayService = inject(LoadingOverlayService);

  constructor() {
    // Gère le retour après redirect MSAL : une fois le profil chargé, on
    // navigue vers la home (qui dispatche ensuite vers le bon dashboard).
    this.authService
      .handleRedirectObservable({ navigateToLoginRequestUrl: false })
      .subscribe(async (result) => {
        if (result) {
          await this.userService.getUserRoles();
          this.router.navigateByUrl('/');
        }
      });

    // Quand MSAL passe en NONE (= plus d'interaction en cours, ex: session
    // déjà active sur un rechargement), on précharge aussi le profil.
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntilDestroyed(),
      )
      .subscribe(async () => {
        const accounts = this.authService.instance.getAllAccounts();
        if (accounts.length > 0) {
          await this.userService.getUserRoles();
        }
      });

    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loadingOverlayService.show();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.loadingOverlayService.hide();
      }
    });
  }
}
